import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Locale, getDirection, t, TranslationKey } from '@/lib/i18n';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  direction: 'rtl' | 'ltr';
  t: (key: TranslationKey) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    try {
      const saved = localStorage.getItem('palette-locale');
      if (saved === 'en' || saved === 'ar') return saved;
    } catch { /* ignore */ }
    return 'ar';
  });

  const direction = getDirection(locale);

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
    try { localStorage.setItem('palette-locale', locale); } catch { /* ignore */ }
  }, [locale, direction]);

  const translate = (key: TranslationKey) => t(key, locale);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, direction, t: translate }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
